import { useState, useEffect, useContext } from 'react';
import { MobiusContext } from '@/components/MobiusProvider';
import getDcsOptions from '@/utils/mobius/getDcsOptions';
import { FormState } from '../types';
/**
 * A custom hook that manages dynamic dropdowns for degree, category, and subject selection.
 *
 * This hook handles:
 * - Loading initial options based on defaults
 * - Cascading dropdown dependencies (e.g., selecting a degree updates category options)
 * - Maintaining form state for both current selections and available options
 * - Handling field changes with proper state updates and dependencies
 *
 * @param initialValues - Optional object containing default/preset values for degree, category, and subject
 * @returns An object containing:
 *   - formState: The current state of the form including selections and available options
 *   - handleFieldChange: A function to update field values and manage dependent dropdowns
 *
 * @example
 * const { formState, handleFieldChange } = useDynamicDropdowns({
 *   degree: 'Bachelor',
 *   category: 'Engineering'
 * });
 *
 * // Update a field and get its numeric ID
 * const degreeId = handleFieldChange('degree', 'Master');
 */
export const useDynamicDropdowns = (initialValues = {}) => {
  // Get available degrees and presets from context
  const { degrees, presets } = useContext(MobiusContext);
  // Combine presets with passed initialValues, with initialValues taking precedence
  const contextualDefaults = { ...presets, ...initialValues };

  const [formState, setFormState] = useState<FormState>({
    current: {
      degree: contextualDefaults.degree || '',
      category: contextualDefaults.category || '',
      subject: contextualDefaults.subject || '',
    },
    options: {
      degree: [],
      category: [],
      subject: [],
    },
  });

  // Load initial options based on defaults
  useEffect(() => {
    if (degrees) {
      // First, get ALL options
      const options = getDcsOptions(formState.current, degrees || []);

      // Update the form state with the options
      setFormState((prev) => ({
        ...prev,
        options: options as FormState['options'],
      }));

      // If we have contextual defaults, make sure to set them in the form state
      if (contextualDefaults.degree) {
        // Set the degree first
        handleFieldChange('degree', contextualDefaults?.degree || '');

        // After setting degree, set category and subject if they exist
        if (contextualDefaults.category) {
          handleFieldChange('category', contextualDefaults?.category || '');

          if (contextualDefaults.subject) {
            handleFieldChange('subject', contextualDefaults?.subject || '');
          }
        }
      }
    }
  }, [degrees]);

  // Handle form field changes
  /**
   * Updates the form state based on field changes and handles cascading resets of dependent fields.
   *
   * @param field - The name of the form field being changed (e.g., 'degree', 'category', 'subject')
   * @param value - The new value for the specified field
   * @returns The numeric ID corresponding to the selected value, if available
   *
   * @remarks
   * This function:
   * 1. Updates the specified field with the new value
   * 2. Resets dependent fields when parent fields change (e.g., changing 'degree' resets both 'category' and 'subject')
   * 3. Recalculates available options for all fields based on the updated selections
   * 4. Updates the form state with the new selections and options
   */
  const handleFieldChange = (field: string, value: string) => {
    // Create a new state object
    const newState = { ...formState };

    // Update the selected value
    newState.current[field as keyof typeof newState.current] = value;

    // Reset dependent fields
    if (field === 'degree') {
      newState.current.category = '';
      newState.current.subject = '';
    } else if (field === 'category') {
      newState.current.subject = '';
    }

    // Update options based on new selections
    if (degrees) {
      const options = getDcsOptions(newState.current, degrees);
      newState.options = options as FormState['options'];
    }

    // Update the form state with the new selections and options
    setFormState(newState);
  };

  return {
    formState,
    handleFieldChange,
  };
};
